import { useState } from 'react';
import { ChevronDown, ChevronRight, Wrench } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';

/**
 * Single-line tool-call header in Claude Code's style:
 *   ⚙ ToolName(brief description)
 * Click to expand the full JSON input.
 *
 * `summary` is a short human-readable summary of the input — for Bash it's
 * the command, for Read/Write/Edit the file path, for MCPs the first
 * meaningful arg. Falls back to '' (header reads just `ToolName()`).
 */
export function Tool({
  name,
  summary,
  input,
  className,
}: {
  name: string;
  summary?: string;
  input: unknown;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className={cn('min-w-0 max-w-full overflow-hidden rounded-md border bg-muted/30', className)}
    >
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center gap-1.5 px-2.5 py-1.5 text-left text-xs"
        >
          {open ? (
            <ChevronDown className="size-3 shrink-0" />
          ) : (
            <ChevronRight className="size-3 shrink-0" />
          )}
          <Wrench className="size-3 shrink-0 text-muted-foreground" />
          <span className="truncate font-mono">
            <span className="font-semibold">{name}</span>
            <span className="text-muted-foreground">({summary ?? ''})</span>
          </span>
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="border-t px-2.5 py-1.5">
        <pre className="overflow-x-auto whitespace-pre-wrap break-all font-mono text-[11px]">
          {JSON.stringify(input, null, 2)}
        </pre>
      </CollapsibleContent>
    </Collapsible>
  );
}
